// StatementPDF.jsx
import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFDownloadLink,
} from "@react-pdf/renderer";

// ---------- Helpers ----------
const fmtMoney = (n, currency = "GBP") =>
  `${currency} ${Number(n || 0).toFixed(2)}`;

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
};

// ---------- Styles ----------
const styles = StyleSheet.create({
  page: {
    paddingTop: 24,
    paddingBottom: 24,
    paddingHorizontal: 28,
    fontSize: 10,
    color: "#111827",
    backgroundColor: "#FFFFFF",
  },
  card: {
    borderRadius: 10,
    borderColor: "#E5E7EB",
    borderWidth: 1,
    padding: 18,
  },
  row: { flexDirection: "row" },
  between: { justifyContent: "space-between" },
  sectionGap: { marginTop: 14 },
  muted: { color: "#6B7280" },
  bold: { fontWeight: 700 },
  h6: { fontSize: 12, fontWeight: 700 },
  small: { fontSize: 9 },
  divider: {
    height: 1,
    backgroundColor: "#F3F4F6",
    marginVertical: 12,
  },

  // Header blocks
  topWrap: { flexDirection: "row", justifyContent: "space-between" },
  periodBlock: {},
  logoBlock: { alignItems: "flex-end" },

  // Info grid
  infoGrid: { flexDirection: "row", gap: 16 },
  infoCol: { flex: 1 },

  // Summary badges
  summary: { marginTop: 8 },
  summaryRow: { flexDirection: "row", gap: 12, flexWrap: "wrap" },
  summaryItem: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
  },

  // Table
  tableHead: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  tableRow: {
    flexDirection: "row",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: "#E5E7EB",
  },
  cell: {
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderColor: "#E5E7EB",
    justifyContent: "center",
  },
  // column widths
  cTid: { width: 90 },
  cRef: { width: 150 },
  cDate: { width: 80 },
  cParty: { width: 120 },
  cMoney: { width: 110, alignItems: "flex-end" },
  cBal: { width: 110, alignItems: "flex-end" },

  inText: { color: "#059669", fontWeight: 700 },  // green
  outText: { color: "#DC2626", fontWeight: 700 }, // red
  pageFooter: {
    marginTop: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    color: "#6B7280",
  },
});

// ---------- Document ----------
export const StatementDocument = ({
  meta,          // { periodFrom, periodTo, logoText }
  account,       // { name, addressLines:[], cityZip, accountNo, iban, currency }
  summary,       // { opening, closing }
  transactions,  // array (see shape below)
}) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.card}>
        {/* Top header */}
        <View style={[styles.topWrap]}>
          <View style={styles.periodBlock}>
            <Text style={[styles.h6, { marginBottom: 4 }]}>Statement Period</Text>
            <Text style={styles.muted}>
              {fmtDate(meta?.periodFrom)} - {fmtDate(meta?.periodTo)}
            </Text>
          </View>
          <View style={styles.logoBlock}>
            <Text style={[styles.h6]}>LOGO Here</Text>
          </View>
        </View>

        <View style={styles.divider} />

        {/* Info grid: Left = customer, Right = summary & account details */}
        <View style={styles.infoGrid}>
          <View style={styles.infoCol}>
            <Text style={styles.h6}>{account?.name}</Text>
            {(account?.addressLines || []).map((ln, i) => (
              <Text key={i} style={styles.muted}>{ln}</Text>
            ))}
            {!!account?.cityZip && <Text style={styles.muted}>{account?.cityZip}</Text>}
          </View>

          <View style={styles.infoCol}>
            <Text style={styles.h6}>Statement Summary</Text>
            <View style={styles.summary}>
              <View style={styles.summaryRow}>
                <View style={styles.summaryItem}>
                  <Text style={styles.small}>Opening Balance</Text>
                  <Text style={styles.bold}>{fmtMoney(summary?.opening, account?.currency)}</Text>
                </View>
                <View style={styles.summaryItem}>
                  <Text style={styles.small}>Closing Balance</Text>
                  <Text style={styles.bold}>{fmtMoney(summary?.closing, account?.currency)}</Text>
                </View>
              </View>
            </View>

            <View style={{ marginTop: 10 }}>
              <Text style={[styles.h6, { color: "#2563EB" }]}>Account Details</Text>
              {!!account?.accountNo && <Text style={styles.muted}>Account No: {account?.accountNo}</Text>}
              {!!account?.iban && <Text style={styles.muted}>IBAN: {account?.iban}</Text>}
              {!!account?.currency && <Text style={styles.muted}>Account Currency: {account?.currency}</Text>}
            </View>
          </View>
        </View>

        <View style={styles.divider} />

        {/* Table header */}
        <View style={styles.tableHead}>
          <View style={[styles.cell, styles.cTid]}><Text style={styles.bold}>T_ID</Text></View>
          <View style={[styles.cell, styles.cRef]}><Text style={styles.bold}>Reference</Text></View>
          <View style={[styles.cell, styles.cDate]}><Text style={styles.bold}>Date</Text></View>
          <View style={[styles.cell, styles.cParty]}><Text style={styles.bold}>Creditor/Debtor</Text></View>
          <View style={[styles.cell, styles.cMoney]}><Text style={styles.bold}>Money +In / -Out</Text></View>
          <View style={[styles.cell, styles.cBal]}><Text style={styles.bold}>Closing Balance</Text></View>
        </View>

        {/* Table rows */}
        {transactions?.map((tx, i) => {
          const isIn = Number(tx.amount) >= 0;
          const moneyText = `${isIn ? "+" : "-"} ${fmtMoney(Math.abs(tx.amount), account?.currency)}`;
          return (
            <View key={i} style={styles.tableRow}>
              <View style={[styles.cell, styles.cTid]}>
                <Text style={styles.bold}>{tx.tid}</Text>
              </View>
              <View style={[styles.cell, styles.cRef]}>
                <Text>{tx.reference}</Text>
                {!!tx.refNote && <Text style={styles.muted}>{tx.refNote}</Text>}
              </View>
              <View style={[styles.cell, styles.cDate]}>
                <Text>{fmtDate(tx.date)}</Text>
                {!!tx.time && <Text style={styles.muted}>{tx.time}</Text>}
              </View>
              <View style={[styles.cell, styles.cParty]}>
                <Text>{tx.party}</Text>
                {!!tx.partyRef && <Text style={styles.muted}>{tx.partyRef}</Text>}
              </View>
              <View style={[styles.cell, styles.cMoney]}>
                <Text style={isIn ? styles.inText : styles.outText}>{moneyText}</Text>
              </View>
              <View style={[styles.cell, styles.cBal]}>
                <Text>{fmtMoney(tx.closingBalance, account?.currency)}</Text>
              </View>
            </View>
          );
        })}

        {/* Footer */}
        <View style={styles.pageFooter}>
          <Text>Copyright © MTBS.co 2025</Text>
          <Text>Page 1</Text>
        </View>
      </View>
    </Page>
  </Document>
);

// ---------- Convenience wrapper with a button ----------
export const StatementPDFButton = ({ fileName = "statement.pdf", ...props }) => (
{/* <PDFDownloadLink
  document={<StatementDocument {...props} />}
  fileName={fileName}
  style={{
    textDecoration: "none",
    backgroundColor: "#2563EB",
    color: "#fff",
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 14,
    paddingRight: 14,
    borderRadius: 8,
    fontSize: 12,
  }}
>
  {({ loading }) => (loading ? "Preparing..." : "Download PDF")}
</PDFDownloadLink> */}
);
